import React, { ChangeEvent, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { deleteUploadedFile, uploadFile } from "../../api/fileUploadService";
import { getImageUrl } from "../../utils/imageUrl";

interface AssetUploadFieldProps {
  label: string;
  value?: string;
  context: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const AssetUploadField: React.FC<AssetUploadFieldProps> = ({
  label,
  value = "",
  context,
  onChange,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadedKeyRef = useRef<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const id = `asset-upload-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  const cleanupNewUpload = () => {
    const key = uploadedKeyRef.current;
    if (key) {
      void deleteUploadedFile(key, context).catch(() => undefined);
      uploadedKeyRef.current = null;
    }
  };

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }

    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadFile(file, context);
      cleanupNewUpload();
      uploadedKeyRef.current = uploaded.key;
      onChange(getImageUrl(uploaded.key));
    } catch (err: any) {
      setError(err?.message || "The image could not be uploaded.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => {
    cleanupNewUpload();
    onChange("");
  };

  return (
    <Form.Group>
      <Form.Label htmlFor={id}>{label}</Form.Label>
      <div className="d-flex align-items-center gap-2">
        <Form.Control
          ref={inputRef}
          id={id}
          aria-label={label}
          type="file"
          accept="image/*"
          onChange={handleUpload}
          disabled={disabled || uploading}
        />
        {value && (
          <Button type="button" variant="outline-danger" onClick={clear} disabled={disabled || uploading}>
            Remove
          </Button>
        )}
      </div>
      {uploading && <Form.Text>Uploading image…</Form.Text>}
      {error && <Form.Text className="text-danger d-block">{error}</Form.Text>}
      {value && (
        <img
          src={getImageUrl(value)}
          alt={`${label} preview`}
          className="d-block mt-2 border rounded"
          style={{ width: 72, height: 72, objectFit: "cover" }}
        />
      )}
    </Form.Group>
  );
};

export default AssetUploadField;
